/*!
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

import { ReactElement, useEffect, useRef } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { Outlet } from 'react-router-dom';

export interface RequiresAuthProps {
  loadingElement: ReactElement;
}

export function RequiresAuth({ loadingElement }: RequiresAuthProps) {
  const { oktaAuth, authState } = useOktaAuth();
  const hasStartedLoginRedirect = useRef(false);

  useEffect(() => {
    async function startLoginRedirect() {
      try {
        await oktaAuth.signInWithRedirect();
      } catch {
        hasStartedLoginRedirect.current = false;
      }
    }

    if (!authState) {
      return;
    }

    if (authState.isAuthenticated) {
      hasStartedLoginRedirect.current = false;
      return;
    }

    if (!hasStartedLoginRedirect.current) {
      hasStartedLoginRedirect.current = true;
      const originalUri = toRelativeUrl(window.location.href, window.location.origin);
      oktaAuth.setOriginalUri(originalUri);
      startLoginRedirect();
    }
  }, [oktaAuth, authState, authState?.isAuthenticated]);

  if (!authState || !authState.isAuthenticated) {
    return loadingElement;
  }

  return <Outlet />;
}
